'use client';

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Button, Input, Chip } from '@heroui/react';
import { Typography, UniversalTable, CustomHeroButton } from '../../components';
import { ResponsiveTooltip } from '../../components/ui';
import { Link, useNavigate } from 'react-router';
import { useAccount } from '../../contexts/AccountContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePageBreadcrumbs } from '../../hooks/usePageBreadcrumbs';
import { useTranslations } from '../../contexts/translation.context';
import UserIc from '../user.svg';
import { Account, TableColumn } from '../../types/account';
import RefreshIc from './refresh.svg';
import SearchIc from './Search.svg';
import { getTableColumns } from './constants';
import IcDate from '../../components/ic-date.svg';
import IcInfo from '../../components/ic-info.svg';
import { BlueCircleIc, EmptyContentClientIc, InfoCircle } from '../../components/icons';
import { formatTimeAgo } from '../../utils/formatTimeAgo';

const Accounts: React.FC = () => {
  const {
    accounts,
    isLoading,
    pagination,
    search,
    page,
    pageSize,
    sortBy,
    refreshAccounts,
    setSearch,
    setPage,
    setPageSize,
    setSortBy,
  } = useAccount();

  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState(search);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { t } = useTranslations();

  const breadcrumbs = useMemo(
    () => [
      { label: t('sidebar.dashboard'), href: '/client/dashboard' },
      { label: t('sidebar.accounts'), isActive: true },
    ],
    [t]
  );

  usePageBreadcrumbs(breadcrumbs);

  // Debounce для поиска
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchValue !== search) {
        setSearch(searchValue);
        setPage(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchValue, search, setSearch, setPage]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refreshAccounts();
    } finally {
      setIsRefreshing(false);
    }
  }, [refreshAccounts]);

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      setPageSize(newPageSize);
      setPage(1);
    },
    [setPageSize, setPage]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
    },
    [setPage]
  );

  const handleSort = useCallback(
    (sortKey: string) => {
      console.log('[Accounts handleSort] Received sortKey:', sortKey);
      setSortBy(sortKey);
      setPage(1);
    },
    [setSortBy, setPage]
  );

  const columns: TableColumn[] = useMemo(() => getTableColumns(t), [t]);

  // Функция для получения статуса аккаунта
  const getStatusChip = (status: string) => {
    switch (status) {
      case 'wait_media':
        return {
          text: t('accounts.status.waitMedia'),
          color: '#F31260' as const,
          variant: 'flat' as const,
        };
      case 'active':
        return {
          text: t('accounts.status.active'),
          color: '#17C964' as const,
          variant: 'flat' as const,
        };
      case 'moderation':
        return {
          text: t('accounts.status.moderation'),
          color: '#7828C8' as const,
          variant: 'flat' as const,
        };
      case 'deactivated':
        return {
          text: t('accounts.status.deactivated'),
          color: '#71717A' as const,
          variant: 'flat' as const,
        };
      case 'hidden':
        return {
          text: t('accounts.status.hidden'),
          color: '#71717A' as const,
          variant: 'flat' as const,
        };
      default:
        return {
          text: t('accounts.status.unknown'),
          color: 'default' as const,
          variant: 'flat' as const,
        };
    }
  };

  // Функция для получения правильной формы слова "кампания/campaigns"
  const getCampaignsText = (count: number) => {
    const campaigns = t('sidebar.campaigns').toLowerCase();

    // Если язык английский, используем множественное/единственное число
    if (campaigns === 'campaigns') {
      return count === 1 ? 'campaign' : 'campaigns';
    }

    // Для русского языка склоняем
    const lastDigit = count % 10;
    const lastTwoDigits = count % 100;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
      return 'кампаний';
    }
    if (lastDigit === 1) {
      return 'кампания';
    }
    if (lastDigit >= 2 && lastDigit <= 4) {
      return 'кампании';
    }
    return 'кампаний';
  };

  // Функция для форматирования даты
  // const formatDate = (dateString: string) => {
  //   const date = new Date(dateString);
  //   const now = new Date();
  //   const diffTime = Math.abs(now.getTime() - date.getTime());
  //   const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  //   const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));

  //   if (diffDays > 1) {
  //     return `${diffDays}д. ${diffHours % 24}ч. назад`;
  //   } else {
  //     return `${diffHours}ч. назад`;
  //   }
  // };

  // Функция для рендеринга контента тултипа
  const renderTooltipContent = (account: Account) => {
    const ofData = account?.of_data || {};

    const parseNumber = (value: unknown) => {
      if (value === null || value === undefined) return 0;
      if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue : 0;
    };

    const formatCurrency = (value: unknown) => `$${parseNumber(value).toFixed(2)}`;
    const formatInteger = (value: unknown) => parseNumber(value).toLocaleString();

    const balance = formatCurrency(ofData?.balance);
    const subscribers = formatInteger(ofData?.subscribers ?? ofData?.subscribers_count);
    const favorited = formatInteger(ofData?.favorited ?? ofData?.likes_count);
    const posts = formatInteger(ofData?.posts ?? ofData?.posts_count);
    const blockedCountriesCount = Array.isArray(ofData?.blocked_countries)
      ? ofData.blocked_countries.length
      : 0;
    const blockedCountries = blockedCountriesCount.toLocaleString();

    return (
      <div className="bg-white border border-light rounded-3 p-0 min-w-[17.5rem]">
        {/* Header */}
        <div className="px-4 pt-4 pb-0">
          <Typography
            variant="body2"
            className="text-auth-ink text-sm font-normal leading-[1.125rem]"
          >
            {t('accounts.generalInfo')}
          </Typography>
        </div>

        {/* Stats */}
        <div className="flex flex-col gap-1 px-4 py-0">
          {/* Balance */}
          <div className="flex items-center justify-between py-1.5">
            <Typography variant="body3" className="text-neutral-600 text-xs font-normal leading-4">
              {t('accounts.balance')}
            </Typography>
            <Typography variant="body3" className="text-neutral-900 text-xs font-medium leading-4">
              {balance}
            </Typography>
          </div>

          {/* Subscribers */}
          <div className="flex items-center justify-between py-1.5">
            <Typography variant="body3" className="text-neutral-600 text-xs font-normal leading-4">
              {t('accounts.subscribers')}
            </Typography>
            <Typography variant="body3" className="text-neutral-900 text-xs font-medium leading-4">
              {subscribers}
            </Typography>
          </div>

          {/* Favorited */}
          <div className="flex items-center justify-between py-1.5">
            <Typography variant="body3" className="text-neutral-600 text-xs font-normal leading-4">
              {t('accounts.likes')}
            </Typography>
            <Typography variant="body3" className="text-neutral-900 text-xs font-medium leading-4">
              {favorited}
            </Typography>
          </div>

          {/* Posts */}
          <div className="flex items-center justify-between py-1.5">
            <Typography variant="body3" className="text-neutral-600 text-xs font-normal leading-4">
              {t('accounts.posts')}
            </Typography>
            <Typography variant="body3" className="text-neutral-900 text-xs font-medium leading-4">
              {posts}
            </Typography>
          </div>

          {/* Blocked countries */}
          <div className="flex items-center justify-between py-1.5">
            <Typography variant="body3" className="text-neutral-600 text-xs font-normal leading-4">
              {t('accounts.blockedCountries')}
            </Typography>
            <Typography variant="body3" className="text-neutral-900 text-xs font-medium leading-4">
              {blockedCountries}
            </Typography>
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 pb-4">
          <Typography variant="body4" className="text-neutral-600 text-xxs font-normal leading-4">
            {ofData?.updated_at
              ? t('accounts.updatedAgo', { time: formatTimeAgo(ofData.updated_at, t) })
              : t('accounts.noUpdateInfo')}
          </Typography>
        </div>
      </div>
    );
  };

  // Функция рендеринга ячеек для аккаунтов
  const renderAccountCell = (account: Account, columnKey: string) => {
    const statusChip = getStatusChip(account.status);
    const overallPopularityPercent = (() => {
      const value = account?.of_data?.overall_popularity_percent;
      if (value === null || value === undefined) return 0;
      if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue : 0;
    })();

    switch (columnKey) {
      case 'account':
        return (
          <div className="flex items-start gap-3">
            <div className="min-w-10 mt-[0.1875rem] max-h-10 min-h-10 max-w-10 ">
              <img
                src={account.avatar}
                alt={account.name}
                className="min-w-10 min-h-10 min-h-10 max-w-10 rounded-xl object-cover"
              />
            </div>
            <div className="">
              <div className="flex items-center gap-x-2 gap-y-0 flex-wrap">
                <Typography variant="label2" className="whitespace-nowrap">
                  {account.name}
                </Typography>
                <div className="flex items-center gap-0.5 flex-nowrap">
                  <a
                    target="__blank"
                    href={`https://onlyfans.com/${account.of_user_name}`}
                    className="text-blue-600 hover:underline flex items-center gap-1 whitespace-nowrap"
                  >
                    <Typography variant="body2" className="!text-partner">
                      @{account.of_user_name}
                    </Typography>
                  </a>
                  <ResponsiveTooltip
                    content={renderTooltipContent(account)}
                    placement="bottom"
                    showArrow={false}
                    classNames={{
                      content: 'p-0 bg-transparent shadow-none',
                    }}
                  >
                    <div className="cursor-pointer">
                      <InfoCircle />
                    </div>
                  </ResponsiveTooltip>
                </div>
              </div>

              {/* Status Chips */}
              <div className="flex flex-wrap gap-2 mt-1">
                <ResponsiveTooltip
                  content={t('accounts.subscriptionTypeTooltip')}
                  placement="top"
                >
                  <div>
                    <Chip
                      size="sm"
                      variant="flat"
                      classNames={{
                        base: ' bg-partner/20',
                        content: `text-partner ${account?.type === 'free' ? 'uppercase' : 'capitalize'}`,
                      }}
                    >
                      {account?.type}
                    </Chip>
                  </div>
                </ResponsiveTooltip>
                <ResponsiveTooltip
                  content={t('accounts.popularityTooltip')}
                  placement="top"
                >
                  <div>
                    <Chip
                      size="sm"
                      color="warning"
                      variant="flat"
                      classNames={{
                        base: ' bg-warning/20',
                        content: 'text-warning',
                      }}
                    >
                      {overallPopularityPercent}%
                    </Chip>
                  </div>
                </ResponsiveTooltip>
                <ResponsiveTooltip
                  content={t('accounts.accountStatusTooltip')}
                  placement="top"
                >
                  <div>
                    <Chip
                      size="sm"
                      variant={statusChip.variant}
                      style={{
                        backgroundColor: `${statusChip.color}20`,
                        color: statusChip.color,
                      }}
                    >
                      <span style={{ color: statusChip.color }}>{statusChip.text}</span>
                    </Chip>
                  </div>
                </ResponsiveTooltip>
              </div>
            </div>
          </div>
        );

      case 'revshare':
        // Check if offer exists and its status
        const offer = account.offer;
        if (!offer) {
          return (
            <Chip size="sm" variant="flat" color="default">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-auth-muted rounded-full"></div>
                <span>{t('accounts.noOffers')}</span>
              </div>
            </Chip>
          );
        }

        // Offer exists - show status, commission and last update
        const offerStatus = offer.status?.toLowerCase();
        const statusConfig =
          offerStatus === 'active'
            ? {
                dot: 'bg-success',
                text: t('accounts.offerActive'),
                chipBg: 'bg-auth-surface',
                chipText: 'text-auth-ink',
              }
            : offerStatus === 'moderation'
              ? {
                  dot: 'bg-warning',
                  text: t('accounts.offerModeration'),
                  chipBg: 'bg-[#FFF4E5]',
                  chipText: 'text-warning',
                }
              : offerStatus === 'hidden'
                ? {
                    dot: 'bg-auth-muted',
                    text: t('accounts.offerHidden'),
                    chipBg: 'bg-auth-surface',
                    chipText: 'text-auth-muted',
                  }
                : {
                    dot: 'bg-auth-muted',
                    text: offer.status || t('accounts.noOffers'),
                    chipBg: 'bg-auth-surface',
                    chipText: 'text-auth-muted',
                  };

        return (
          <div className="flex flex-col gap-1">
            <div className="flex gap-2">
              {/* Status chip */}
              <Chip
                size="sm"
                variant="flat"
                classNames={{
                  base: statusConfig.chipBg,
                  content: statusConfig.chipText,
                }}
              >
                <div className="flex items-center gap-1">
                  <div className={`w-2 h-2 ${statusConfig.dot} rounded-full`}></div>
                  <span>{statusConfig.text}</span>
                </div>
              </Chip>

              {/* Commission with tooltip */}
              <div className="flex items-center gap-0.5">
                <Typography variant="body3" className="text-auth-muted text-xs">
                  {offer.commission}%
                </Typography>
                <ResponsiveTooltip
                  content={t('accounts.commissionTooltip') || 'Marketer commission + OnlyU fee'}
                  placement="top"
                  classNames={{
                    content:
                      'bg-white border border-light rounded-2 px-3 py-2 text-xs text-auth-ink shadow-sm',
                  }}
                >
                  <div className="cursor-pointer">
                    <InfoCircle />
                  </div>
                </ResponsiveTooltip>
              </div>
            </div>
            {/* RevShare campaigns count */}
            <div className="flex items-center gap-1">
              <Typography
                variant="body3"
                className="!text-partner text-xs font-regular leading-[1.125rem]"
              >
                {account.ref_share_orders_active_count || 0}
              </Typography>
              <Typography
                variant="body3"
                className="!text-auth-strength-none text-xs font-regular leading-[1.125rem]"
              >
                •
              </Typography>
              <Typography
                variant="body3"
                className="!text-auth-muted text-xs font-regular leading-[1.125rem]"
              >
                {account.ref_share_orders_total_count || 0}{' '}
                {getCampaignsText(account.ref_share_orders_total_count || 0)}
              </Typography>
            </div>
            {/* Last update */}
            {account.granted?.updated_at && (
              <ResponsiveTooltip
                content={
                  <div className="text-xs flex flex-col gap-1">
                    <Typography variant="body3" className="!text-auth-muted">
                      {t('accounts.lastUpdateTooltip') || 'Last data sync'}
                    </Typography>
                    <Typography variant="body2">
                      {new Date(account.granted.updated_at).toLocaleString()}
                    </Typography>
                  </div>
                }
                placement="top"
                classNames={{
                  content: 'bg-white border border-light rounded-2 px-3 py-2 shadow-sm',
                }}
              >
                <div className="flex items-center gap-1 cursor-pointer">
                  <img src={IcDate} alt="Date" />
                  <Typography variant="body3" className="text-neutral-500 text-xxs">
                    {formatTimeAgo(account.granted.updated_at, t)}
                  </Typography>
                  <BlueCircleIc />
                </div>
              </ResponsiveTooltip>
            )}
          </div>
        );

      case 'cpl':
        return (
          <ResponsiveTooltip
            content={t('accounts.companiesCountTooltip')}
            placement="top"
          >
            <div className="flex items-center gap-1">
              <Typography
                variant="body3"
                className="!text-partner text-xs font-regular leading-[1.125rem]"
              >
                {account.orders_in_progress_count || 0}
              </Typography>
              <Typography
                variant="body3"
                className="!text-auth-strength-none text-xs font-regular leading-[1.125rem]"
              >
                •
              </Typography>
              <Typography
                variant="body3"
                className="!text-auth-muted text-xs font-regular leading-[1.125rem]"
              >
                {account.orders_count || 0} {getCampaignsText(account.orders_count || 0)}
              </Typography>
            </div>
          </ResponsiveTooltip>
        );

      case 'created':
        return (
          <div className="flex items-center gap-1">
            <img src={IcDate} alt="Date" />
            <Typography variant="body3" className="text-auth-muted">
              {formatTimeAgo(account.created_at, t)}
            </Typography>
          </div>
        );

      case 'actions':
        return (
          <CustomHeroButton
            variant="secondary"
            title={t('labels.open')}
            className="py-2 !px-[2.4063rem] h-8 w-full"
            customStyles={{ fontWeight: 400 }}
            onPress={() => navigate(`/client/accounts/${account.of_user_id}`)}
          />
        );
      default:
        return null;
    }
  };

  // Кастомное пустое состояние для поиска
  const customEmptyContent = search ? (
    <div className="text-center py-12">
      <div className="flex flex-col items-center gap-4">
        <EmptyContentClientIc />
        <div className="flex flex-col gap-1.5">
          <Typography variant="h6" className="text-xl font-semibold leading-7">
            {t('accounts.accountNotFound')}
          </Typography>
          <Typography className="text-neutral-500 text-base font-medium leading-6" variant="body1">
            {t('accounts.checkDataOrTryAgain')}
          </Typography>
        </div>
      </div>
    </div>
  ) : undefined;

  // Show loading spinner only when data is actually being fetched (not during auth)
  if (authLoading || (isLoading && isAuthenticated)) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }
  return (
    <div className="flex flex-col h-full">
      <div className="px-4 pt-6 md:px-6 md:py-4 md:pb-1.5 flex flex-col flex-1 h-screen">
        {(accounts?.length === 0 || !accounts) && !search ? null : (
          <div className="mb-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5">
              <div className="flex items-center gap-2">
                <Typography variant="h5" className="text-gray-900">
                  {t('dashboard.accountsOnlyFans')}
                </Typography>
                <div className="bg-gray-200 rounded-full px-2.5 py-0.5">
                  <Typography variant="body2" className="text-gray-600">
                    {pagination?.count || 0}
                  </Typography>
                </div>
              </div>
              <CustomHeroButton
                title={t('dashboard.addAccount')}
                variant="primary"
                size="md"
                onPress={() => navigate('/client/accounts/create')}
              />
            </div>

            {/* Search and Filter */}
            <div className="flex items-center gap-4">
              <div className="relative w-full md:max-w-xs">
                <Input
                  type="text"
                  placeholder={t('placeholders.search')}
                  size="sm"
                  value={searchValue}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  endContent={<img src={SearchIc} alt="Search" className="w-5 h-5" />}
                  classNames={{
                    input: 'text-sm',
                    inputWrapper: 'rounded-lg h-9 md:w-[17.0625rem] pl-3',
                  }}
                />
              </div>
              {/* <Button
                //@ts-ignore
                size="xs"
                className="h-9 rounded-2 !w-9 p-0 bg-auth-surface"
                onPress={handleRefresh}
                isLoading={isRefreshing}
              >
                <img src={RefreshIc} alt="Refresh" />
              </Button> */}
            </div>
          </div>
        )}
        {/* Content Area - Flex Grow */}
        <div className=" h-full flex flex-col">
          {accounts?.length === 0 || !accounts ? (
            search ? (
              // Если есть поиск и нет результатов - показываем таблицу с пустым состоянием
              <div className="flex-1 flex flex-col">
                <UniversalTable
                  data={[]}
                  columns={columns}
                  renderCell={renderAccountCell}
                  emptyContent={customEmptyContent}
                  isSearchEmpty={true}
                  sortBy={sortBy}
                  onSort={handleSort}
                />
              </div>
            ) : (
              // Если нет поиска и нет аккаунтов - показываем блок "У вас нет аккаунтов"
              <div className="flex flex-col text-center items-center justify-center gap-4 py-2 md:p-16">
                <div className="icon size-12 md:size-[6.25rem]">
                  <img src={UserIc} alt="No accounts" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Typography variant="h6" className="max-md:font-semibold">
                    {t('dashboard.noAccounts')}
                  </Typography>
                  <Typography className="text-auth-muted" variant="body1">
                    {t('dashboard.followSteps')}
                  </Typography>
                </div>
                <CustomHeroButton
                  title={t('dashboard.addAccount')}
                  variant="primary"
                  className="h-12 px-5 !text-base !leading-6 !font-semibold mt-2 py-3 max-md:mt-2"
                  size="md"
                  onPress={() => navigate('/client/accounts/create')}
                />
              </div>
            )
          ) : (
            <div className="h-full flex flex-col">
              {/* Accounts Table */}
              <UniversalTable
                data={accounts}
                columns={columns}
                renderCell={renderAccountCell}
                emptyContent={customEmptyContent}
                isSearchEmpty={!!search}
                pagination={
                  pagination
                    ? {
                        count: pagination.count,
                        page_size: pagination.page_size,
                        page: pagination.page,
                      }
                    : undefined
                }
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                currentPage={page}
                currentPageSize={pageSize}
                sortBy={sortBy}
                onSort={handleSort}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Accounts;
